import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Archive,
  Calendar,
  Users,
  Search,
  Trash2,
  BarChart3,
  Eye,
  ChevronRight
} from "lucide-react";
import { Progress } from "../components/ui/progress";

export default function AttendanceArchivePage() {
  const { token, hasRole } = useAuth();
  const [archives, setArchives] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailSearch, setDetailSearch] = useState("");

  const [searchQuery, setSearchQuery] = useState("");
  const [tab, setTab] = useState("arhiv");
  const statusCycle = ["prisoten", "odsoten", "neznan"];
  const statusLabels = {
    prisoten: "Prisoten", odsoten: "Odsoten", pridem: "Pridem", ne_pridem: "Ne pridem", neznan: "Neznano"
  };
  const statusColors = {
    prisoten: "bg-green-500 text-white hover:bg-green-600",
    pridem: "bg-green-100 text-green-800 hover:bg-green-200",
    odsoten: "bg-red-500 text-white hover:bg-red-600",
    ne_pridem: "bg-red-100 text-red-800 hover:bg-red-200",
    neznan: "bg-gray-200 text-gray-700 hover:bg-gray-300"
  };

  const handleStatusChange = async (archiveId, userId, currentStatus) => {
    const idx = statusCycle.indexOf(currentStatus);
    const newStatus = statusCycle[(idx + 1) % statusCycle.length];
    try {
      const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
      const res = await axios.put(
        `${API}/arhiv-prisotnosti/${archiveId}/attendance/${userId}`,
        { status: newStatus },
        { headers }
      );
      // Update local state
      setDetailData(prev => ({
        ...prev,
        summary: res.data.summary,
        attendance: prev.attendance.map(a =>
          a.user_id === userId ? { ...a, status: newStatus } : a
        )
      }));
      // Refresh archives list and stats
      fetchArchives();
      fetchStats();
    } catch (error) {
      toast.error("Napaka pri spremembi statusa");
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const fetchArchives = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/arhiv-prisotnosti?leto=${selectedYear}`, { headers });
      setArchives(res.data);
    } catch (error) {
      console.error("Error fetching archives:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/arhiv-prisotnosti/statistika/letna?leto=${selectedYear}`, { headers });
      setStats(res.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchArchives();
    fetchStats();
  }, [selectedYear]);

  const openDetail = async (archiveId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/arhiv-prisotnosti/${archiveId}`, { headers });
      setDetailData(res.data);
      setDetailOpen(true);
    } catch (error) {
      toast.error("Napaka pri nalaganju podrobnosti");
    }
  };

  const handleDelete = async (archiveId) => {
    if (!window.confirm("Ali ste prepričani, da želite izbrisati ta arhiv?")) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/arhiv-prisotnosti/${archiveId}`, { headers });
      toast.success("Arhiv izbrisan");
      fetchArchives();
      fetchStats();
    } catch (error) {
      toast.error("Napaka pri brisanju");
    }
  };

  const canDelete = hasRole(["administrator", "predsednik"]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("sl-SI", { day: "numeric", month: "short", year: "numeric" });
  };

  const tipLabels = {
    dogodek: "Dogodek",
    sestanek: "Sestanek",
    lov: "Lov",
    preizkus: "Preizkus orozja",
    delovna_akcija: "Delovna akcija"
  };

  const tipColors = {
    dogodek: "bg-blue-100 text-blue-800",
    sestanek: "bg-purple-100 text-purple-800",
    lov: "bg-green-100 text-green-800",
    preizkus: "bg-amber-100 text-amber-800",
    delovna_akcija: "bg-orange-100 text-orange-800"
  };

  const filteredStats = stats?.clani?.filter(c => {
    const fullName = `${c.ime} ${c.priimek}`.toLowerCase();
    return fullName.includes(searchQuery.toLowerCase());
  }) || [];

  return (
    <div className="space-y-6" data-testid="attendance-archive-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Arhiv prisotnosti</h1>
          <p className="text-gray-600">Pregled arhiviranih list prisotnosti in statistika</p>
        </div>
        <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
          <SelectTrigger className="w-[130px]" data-testid="year-selector">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {years.map(y => (
              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <Button
          variant={tab === "arhiv" ? "default" : "outline"}
          onClick={() => setTab("arhiv")}
          data-testid="tab-arhiv"
        >
          <Archive className="h-4 w-4 mr-2" />
          Arhiv ({archives.length})
        </Button>
        <Button
          variant={tab === "statistika" ? "default" : "outline"}
          onClick={() => setTab("statistika")}
          data-testid="tab-statistika"
        >
          <BarChart3 className="h-4 w-4 mr-2" />
          Statistika
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Nalagam...</div>
      ) : tab === "arhiv" ? (
        /* Archive list */
        archives.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <Archive className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Ni arhiviranih zapisov za leto {selectedYear}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {archives.map((a) => (
              <Card key={a.id} className="hover:shadow-md transition-shadow">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold truncate">{a.event_naslov}</h3>
                        <Badge className={tipColors[a.event_tip] || "bg-gray-100 text-gray-800"}>
                          {tipLabels[a.event_tip] || a.event_tip}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          {formatDate(a.event_datum)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="h-3.5 w-3.5" />
                          {a.summary?.prisoten || 0} prisotnih / {a.skupaj_clanov} članov
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Arhiviral: {a.arhiviral_ime} • {formatDate(a.arhivirano)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => openDetail(a.id)} data-testid={`view-archive-${a.id}`}>
                        <Eye className="h-4 w-4 mr-1" />
                        Podrobnosti
                      </Button>
                      {canDelete && (
                        <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDelete(a.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )
      ) : (
        /* Statistics tab */
        <div className="space-y-4">
          {stats && stats.skupaj_dogodkov > 0 ? (
            <>
              <Card>
                <CardContent className="py-4">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-3xl font-bold text-primary">{stats.skupaj_dogodkov}</p>
                      <p className="text-sm text-muted-foreground">Arhiviranih dogodkov</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-green-600">{stats.clani?.length || 0}</p>
                      <p className="text-sm text-muted-foreground">Evidentiranih članov</p>
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      <p className="text-3xl font-bold text-blue-600">
                        {stats.clani?.length > 0
                          ? Math.round(stats.clani.reduce((s, c) => s + c.udelezba_pct, 0) / stats.clani.length)
                          : 0}%
                      </p>
                      <p className="text-sm text-muted-foreground">Povprečna udeležba</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Išči člana..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Udeležba po članih - {selectedYear}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="max-h-[500px]">
                    <div className="space-y-3">
                      {filteredStats.map((c) => (
                        <div key={c.user_id} className="flex items-center gap-3 py-2 border-b last:border-0">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm">{c.priimek} {c.ime}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Progress value={c.udelezba_pct} className="h-2 flex-1" />
                              <span className="text-xs font-medium w-10 text-right">{c.udelezba_pct}%</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground whitespace-nowrap">
                            <span className="text-green-600 font-medium">{c.prisoten + c.pridem} P</span>
                            <span className="text-red-600 font-medium">{c.odsoten + c.ne_pridem} O</span>
                            <span className="text-gray-400">{c.neznan} N</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Ni statistike za leto {selectedYear}</p>
                <p className="text-sm mt-1">Arhivirajte prisotnost pri dogodkih za prikaz statistike</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={(open) => { setDetailOpen(open); if (!open) setDetailSearch(""); }}>
        <DialogContent className="max-w-2xl mx-4">
          <DialogHeader>
            <DialogTitle>{detailData?.event_naslov}</DialogTitle>
          </DialogHeader>
          {detailData && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                {formatDate(detailData.event_datum)}
                {detailData.event_lokacija && (
                  <span>• {detailData.event_lokacija}</span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2 text-center text-sm">
                <div className="p-2 rounded bg-green-50">
                  <p className="font-bold text-green-600">{(detailData.summary?.prisoten || 0) + (detailData.summary?.pridem || 0)}</p>
                  <p className="text-xs text-green-700">Prisotni</p>
                </div>
                <div className="p-2 rounded bg-red-50">
                  <p className="font-bold text-red-600">{(detailData.summary?.odsoten || 0) + (detailData.summary?.ne_pridem || 0)}</p>
                  <p className="text-xs text-red-700">Odsotni</p>
                </div>
                <div className="p-2 rounded bg-gray-50">
                  <p className="font-bold text-gray-600">{detailData.summary?.neznan || 0}</p>
                  <p className="text-xs text-gray-700">Neznano</p>
                </div>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Išči člana..."
                  value={detailSearch}
                  onChange={(e) => setDetailSearch(e.target.value)}
                  className="pl-9"
                  data-testid="detail-search"
                />
              </div>
              <p className="text-xs text-muted-foreground">Kliknite na status za spremembo (Prisoten → Odsoten → Neznano)</p>
              <ScrollArea className="h-[350px] border rounded-lg">
                <div className="p-2 space-y-1">
                  {detailData.attendance
                    ?.filter(m => {
                      if (!detailSearch) return true;
                      return `${m.ime} ${m.priimek}`.toLowerCase().includes(detailSearch.toLowerCase());
                    })
                    .map((m) => (
                    <div key={m.user_id} className="flex items-center justify-between p-2 rounded hover:bg-muted/50">
                      <span className="text-sm font-medium">{m.priimek} {m.ime}</span>
                      <div className="flex items-center gap-2">
                        {m.vloga_lov && (
                          <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-300">
                            {m.vloga_lov}
                          </Badge>
                        )}
                        {canDelete ? (
                          <Badge
                            className={`cursor-pointer select-none transition-colors ${statusColors[m.status] || statusColors.neznan}`}
                            onClick={() => handleStatusChange(detailData.id, m.user_id, m.status)}
                            data-testid={`status-badge-${m.user_id}`}
                          >
                            {statusLabels[m.status] || "Neznano"}
                          </Badge>
                        ) : (
                          <Badge className={
                            m.status === "prisoten" || m.status === "pridem"
                              ? "bg-green-100 text-green-800"
                              : m.status === "odsoten" || m.status === "ne_pridem"
                              ? "bg-red-100 text-red-800"
                              : "bg-gray-100 text-gray-800"
                          }>
                            {statusLabels[m.status] || "Neznano"}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDetailOpen(false); setDetailSearch(""); }}>Zapri</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
